import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import type { Falha } from '../../acesso';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  fecharConferencia,
  gravarRascunho,
  horaBrasilia,
  lerNumero,
  lerParaConferir,
  lerRascunho,
  nomeBonito,
  qtd,
} from '../../geladeira';
import type { ConferenciaDaGeladeira, ParaConferir, Rascunho } from '../../geladeira';
import './geladeira.css';

/**
 * CONFERIR GELADEIRA — a tela do Iggor (dono 19/10/2026: "ele chega e já faz").
 *
 * Dois números por produto, na ordem da lista dele no WhatsApp: a GELADEIRA DA FARMÁCIA e o
 * ESTOQUE DE CIMA. Ele NÃO vê o saldo do HOS enquanto conta (senão copia o número); o HOS é lido
 * no instante em que ele fecha, e aí a tela mostra o que bateu e o que não bateu — o mesmo
 * resultado que vai para o Telegram do Nicolas e do Carlos.
 *
 * O que ele digitou fica no aparelho até fechar: fechou o app sem querer, não perde a contagem.
 */

type Valores = Record<string, { farmacia: string; estoque: string }>;

const hojeBrasilia = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

function textoDaFalha(f: Falha): string {
  if (f.tipo === 'offline')
    return 'Sem conexão. A contagem continua guardada no aparelho — tente de novo.';
  if (f.tipo === 'sem-acesso') return 'Este acesso não faz a conferência da geladeira.';
  if (f.tipo === 'sem-sessao') return 'A sessão expirou. Entre de novo (a contagem fica guardada).';
  if (f.tipo === 'invalido' || f.tipo === 'conflito')
    return f.mensagem || 'O servidor não aceitou.';
  return 'Não deu certo agora. A contagem continua guardada — tente de novo.';
}

export function ConferirGeladeira({ sessao }: { sessao: Sessao }) {
  const [dados, setDados] = useState<ParaConferir | null>(null);
  const [erro, setErro] = useState(false);
  const [modo, setModo] = useState<'contar' | 'resultado'>('contar');
  const [resultado, setResultado] = useState<ConferenciaDaGeladeira | null>(null);
  const [valores, setValores] = useState<Valores>({});
  const [obs, setObs] = useState('');
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const primeiroCampo = useRef<HTMLInputElement | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    setDados(null);
    void lerParaConferir().then((r) => {
      if (!r.ok) {
        setErro(true);
        return;
      }
      setDados(r.dados);
      const salvo = lerRascunho(r.dados.dia);
      if (salvo) {
        setRascunho(salvo);
        setValores(salvo.valores);
        setModo('contar');
      } else if (r.dados.ultima) {
        setResultado(r.dados.ultima);
        setModo('resultado');
      }
    });
  }, []);
  useEffect(carregar, [carregar]);

  const produtos = dados?.produtos ?? [];
  const preenchidos = useMemo(
    () =>
      produtos.filter((p) => {
        const v = valores[p.id];
        const a = lerNumero(v?.farmacia ?? '');
        const b = lerNumero(v?.estoque ?? '');
        return a !== null && b !== null && !Number.isNaN(a) && !Number.isNaN(b);
      }).length,
    [produtos, valores],
  );
  const invalidos = produtos.filter((p) => {
    const v = valores[p.id];
    return Number.isNaN(lerNumero(v?.farmacia ?? '')) || Number.isNaN(lerNumero(v?.estoque ?? ''));
  });
  const completo = produtos.length > 0 && preenchidos === produtos.length && !invalidos.length;

  // guarda no aparelho a cada número digitado
  const mudar = (id: string, campo: 'farmacia' | 'estoque', texto: string) => {
    setFalha(null);
    setConfirmando(false);
    const novo: Valores = {
      ...valores,
      [id]: {
        farmacia: valores[id]?.farmacia ?? '',
        estoque: valores[id]?.estoque ?? '',
        [campo]: texto,
      },
    };
    const r: Rascunho = rascunho ?? {
      dia: dados?.dia ?? hojeBrasilia(),
      chave: crypto.randomUUID(),
      iniciadaEm: new Date().toISOString(),
      valores: {},
    };
    const comValores = { ...r, valores: novo };
    setValores(novo);
    setRascunho(comValores);
    gravarRascunho(comValores);
  };
  const passo = (id: string, campo: 'farmacia' | 'estoque', d: number) => {
    const atual = lerNumero(valores[id]?.[campo] ?? '');
    const base = atual === null || Number.isNaN(atual) ? 0 : atual;
    mudar(id, campo, String(Math.max(0, Math.min(9999, base + d))));
  };

  const fechar = async () => {
    if (!dados || !completo || enviando) return;
    const r = rascunho ?? {
      dia: dados.dia,
      chave: crypto.randomUUID(),
      iniciadaEm: new Date().toISOString(),
      valores,
    };
    setEnviando(true);
    setFalha(null);
    const resposta = await fecharConferencia(sessao, {
      chave: r.chave,
      iniciadaEm: r.iniciadaEm,
      observacao: obs.trim() || null,
      itens: produtos.map((p) => ({
        produtoId: p.id,
        farmacia: lerNumero(valores[p.id]?.farmacia ?? '') ?? 0,
        estoque: lerNumero(valores[p.id]?.estoque ?? '') ?? 0,
      })),
    });
    setEnviando(false);
    setConfirmando(false);
    if (!resposta.ok) {
      setFalha(textoDaFalha(resposta.falha));
      return;
    }
    gravarRascunho(null);
    setRascunho(null);
    setValores({});
    setObs('');
    setResultado(resposta.dados);
    setModo('resultado');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const contarDeNovo = () => {
    setResultado(null);
    setValores({});
    setRascunho(null);
    setModo('contar');
    window.scrollTo({ top: 0 });
    setTimeout(() => primeiroCampo.current?.focus(), 50);
  };

  if (erro)
    return (
      <section className="gd">
        <ErroDeArea oQue="a lista da geladeira" aoTentar={carregar} />
      </section>
    );
  if (!dados)
    return (
      <section className="gd" aria-busy="true">
        <div className="gd-carregando" />
      </section>
    );
  if (!produtos.length)
    return (
      <section className="gd">
        <EstadoVazio
          icone="gelo"
          titulo="A lista da geladeira está vazia"
          texto="Peça ao administrador para escolher as canetas e os produtos de alto valor em Geladeira › Produtos."
        />
      </section>
    );

  if (modo === 'resultado' && resultado)
    return <ResultadoDaConferencia c={resultado} aoContarDeNovo={contarDeNovo} />;

  return (
    <section className="gd gd-conferir">
      <header className="gd-cabeca">
        <span className="gd-selo" aria-hidden="true">
          <Icone nome="gelo" tamanho={22} traco={1.6} />
        </span>
        <div>
          <h1>Conferir geladeira</h1>
          <p className="gd-sub">
            Conte a <b>geladeira da farmácia</b> e o <b>estoque de cima</b>. O HOS é conferido
            quando você fechar.
          </p>
        </div>
      </header>

      <div
        className="gd-progresso"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={produtos.length}
        aria-valuenow={preenchidos}
        aria-label="Produtos contados"
      >
        <div
          className="gd-progresso-barra"
          style={{ width: `${String((preenchidos / produtos.length) * 100)}%` }}
        />
        <span>
          {preenchidos} de {produtos.length} contados
        </span>
      </div>

      {dados.vendidosOntem && <VendidosOntem v={dados.vendidosOntem} />}

      {rascunho && preenchidos > 0 && (
        <div className="gd-aviso gd-aviso-info" role="status">
          <Icone nome="clock" tamanho={15} />
          <span>
            Contagem começada às {horaBrasilia(rascunho.iniciadaEm)} — guardada neste aparelho até
            você fechar.
          </span>
        </div>
      )}

      <ol className="gd-lista">
        {produtos.map((p, i) => {
          const v = valores[p.id];
          const a = lerNumero(v?.farmacia ?? '');
          const b = lerNumero(v?.estoque ?? '');
          const ok = a !== null && b !== null && !Number.isNaN(a) && !Number.isNaN(b);
          const ruim = Number.isNaN(a) || Number.isNaN(b);
          return (
            <li
              key={p.id}
              className={`gd-item${ok ? ' gd-item-ok' : ''}${ruim ? ' gd-item-ruim' : ''}`}
            >
              <div className="gd-item-nome">
                <span className="gd-item-num">{i + 1}</span>
                <div>
                  <strong>{p.nome}</strong>
                  {p.descricaoHos && <small>{p.descricaoHos}</small>}
                </div>
                {ok && (
                  <span className="gd-item-total" aria-label={`Total ${qtd((a ?? 0) + (b ?? 0))}`}>
                    {qtd((a ?? 0) + (b ?? 0))}
                  </span>
                )}
              </div>
              <div className="gd-campos">
                {(['farmacia', 'estoque'] as const).map((campo) => (
                  <label key={campo} className="gd-campo">
                    <span>{campo === 'farmacia' ? 'Farmácia' : 'Estoque de cima'}</span>
                    <div className="gd-passo">
                      <button
                        type="button"
                        onClick={() => passo(p.id, campo, -1)}
                        aria-label={`Menos 1 em ${campo === 'farmacia' ? 'farmácia' : 'estoque'} de ${p.nome}`}
                      >
                        <Icone nome="minus" tamanho={16} traco={2} />
                      </button>
                      <input
                        ref={i === 0 && campo === 'farmacia' ? primeiroCampo : undefined}
                        inputMode="decimal"
                        autoComplete="off"
                        enterKeyHint="next"
                        placeholder="—"
                        value={v?.[campo] ?? ''}
                        onChange={(e) => mudar(p.id, campo, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        aria-invalid={Number.isNaN(lerNumero(v?.[campo] ?? ''))}
                      />
                      <button
                        type="button"
                        onClick={() => passo(p.id, campo, 1)}
                        aria-label={`Mais 1 em ${campo === 'farmacia' ? 'farmácia' : 'estoque'} de ${p.nome}`}
                      >
                        <Icone nome="plus" tamanho={16} traco={2} />
                      </button>
                    </div>
                  </label>
                ))}
              </div>
            </li>
          );
        })}
      </ol>

      <label className="gd-obs">
        <span>Observação (opcional)</span>
        <textarea
          value={obs}
          maxLength={500}
          rows={2}
          placeholder="Ex.: 1 Mounjaro 5mg separado no balcão para cliente"
          onChange={(e) => setObs(e.target.value)}
        />
      </label>

      <div className="gd-rodape">
        {falha && (
          <div className="gd-aviso gd-aviso-erro" role="alert">
            <Icone nome="warn" tamanho={15} />
            <span>{falha}</span>
          </div>
        )}
        {invalidos.length > 0 && (
          <div className="gd-aviso gd-aviso-erro" role="status">
            <Icone nome="warn" tamanho={15} />
            <span>
              Número inválido em {invalidos.map((p) => p.nome).join(', ')}. Use só números (ex.: 2).
            </span>
          </div>
        )}
        {confirmando ? (
          <div className="gd-confirma" role="group" aria-label="Confirmar fechamento">
            <p>
              Fechar com <b>{produtos.length}</b> produtos contados? O HOS é lido agora e o
              resultado vai para o Telegram.
            </p>
            <div>
              <button
                type="button"
                className="gd-btn gd-btn-sec"
                onClick={() => setConfirmando(false)}
                disabled={enviando}
              >
                Voltar
              </button>
              <button
                type="button"
                className="gd-btn"
                onClick={() => void fechar()}
                disabled={enviando}
              >
                {enviando ? 'Lendo o HOS…' : 'Fechar conferência'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="gd-btn gd-btn-largo"
            disabled={!completo}
            onClick={() => setConfirmando(true)}
          >
            <Icone nome="check" tamanho={18} traco={2} />
            {completo
              ? 'Fechar conferência'
              : `Faltam ${String(produtos.length - preenchidos)} para fechar`}
          </button>
        )}
      </div>
    </section>
  );
}

/** O resultado (a mesma conta que vai ao Telegram): o que não bateu primeiro. */
export function ResultadoDaConferencia({
  c,
  aoContarDeNovo,
}: {
  c: ConferenciaDaGeladeira;
  aoContarDeNovo?: () => void;
}) {
  const dif = c.contagens.filter((k) => k.diferenca !== null && k.diferenca !== 0);
  const semHos = c.contagens.filter((k) => k.diferenca === null);
  const ordem = [...dif, ...semHos, ...c.contagens.filter((k) => k.diferenca === 0)];
  const tudo = !dif.length && !semHos.length;
  return (
    <section className="gd gd-conferir">
      <div
        className={`gd-veredito ${tudo ? 'gd-veredito-ok' : dif.length ? 'gd-veredito-erro' : 'gd-veredito-aviso'}`}
        role="status"
      >
        <span className="gd-veredito-icone" aria-hidden="true">
          <Icone nome={tudo ? 'check' : 'warn'} tamanho={26} traco={2} />
        </span>
        <div>
          <h1>
            {tudo
              ? 'Tudo bateu com o HOS'
              : dif.length
                ? `${String(dif.length)} ${dif.length === 1 ? 'produto não bateu' : 'produtos não bateram'}`
                : 'O HOS não respondeu para alguns'}
          </h1>
          <p>
            {nomeBonito(c.por.nome) ?? c.por.nome} fechou às {horaBrasilia(c.fechadaEm)} ·{' '}
            {c.contagens.length} produtos · já foi para o Telegram.
          </p>
        </div>
      </div>

      <ul className="gd-resultado">
        {ordem.map((k) => {
          const d = k.diferenca;
          const classe = d === null ? 'gd-r-cinza' : d === 0 ? 'gd-r-ok' : 'gd-r-erro';
          return (
            <li key={k.produtoId} className={classe}>
              <div className="gd-r-nome">
                <strong>{k.nome}</strong>
                <small>
                  farmácia {qtd(k.farmacia)} + estoque {qtd(k.estoque)}
                </small>
              </div>
              <div className="gd-r-nums">
                <span>
                  <small>Contou</small>
                  <b>{qtd(k.total)}</b>
                </span>
                <span>
                  <small>HOS</small>
                  <b>{k.hosSaldo === null ? '—' : qtd(k.hosSaldo)}</b>
                </span>
              </div>
              <span className="gd-chip">
                {d === null
                  ? 'HOS sem leitura'
                  : d === 0
                    ? 'Bateu'
                    : d > 0
                      ? `Sobra ${qtd(d)}`
                      : `Falta ${qtd(-d)}`}
              </span>
            </li>
          );
        })}
      </ul>
      {c.observacao && (
        <p className="gd-obs-lida">
          <Icone nome="edit" tamanho={14} /> {c.observacao}
        </p>
      )}
      {aoContarDeNovo && (
        <div className="gd-rodape gd-rodape-solto">
          <button type="button" className="gd-btn gd-btn-sec gd-btn-largo" onClick={aoContarDeNovo}>
            <Icone nome="retry" tamanho={16} />
            Contar de novo
          </button>
          <p className="gd-nota">
            Recontar não apaga esta: as duas ficam na história e vale a última do dia.
          </p>
        </div>
      )}
    </section>
  );
}

/** Dono 26/10: o Higor vê o que da lista saiu no dia anterior (quanto e quem vendeu, sem saldo). */
function VendidosOntem({ v }: { v: NonNullable<ParaConferir['vendidosOntem']> }) {
  const data = `${v.dia.slice(8, 10)}/${v.dia.slice(5, 7)}`;
  return (
    <section className="gd-ontem" aria-label={`Vendidos em ${data}`}>
      <h2>
        <Icone nome="receipt" tamanho={15} />
        Vendidos ontem ({data})
      </h2>
      {v.itens.length === 0 ? (
        <p className="gd-ontem-nada">Nada da lista foi vendido ontem.</p>
      ) : (
        <ul>
          {v.itens.map((i) => (
            <li key={i.produtoId}>
              <strong>{qtd(i.quantidade)}×</strong>
              <span>
                {i.nome}
                <small>
                  {i.vendas
                    .map(
                      (x) =>
                        `${horaBrasilia(x.instante)} ${x.vendedor ?? 'sem vendedor'}${x.quantidade !== 1 ? ` (${qtd(x.quantidade)})` : ''}`,
                    )
                    .join(' · ')}
                </small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
